/**
 * Primitivas visuales.
 *
 * Se concentran aqui para que toda la aplicacion se vea igual sin repetir
 * listas de clases. Cualquier control interactivo cumple el area tactil minima
 * de 48 px: la caja se opera con el dedo, con prisa y a veces con la otra mano
 * ocupada.
 */

import {
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, ChevronDown, ChevronRight, Loader2, RefreshCw, X } from 'lucide-react';
import { ScanLayerContext, useScanBlocker, useScanLayer } from '../lib/scanner.js';
import { useFrameKeys } from '../lib/keys.js';
import { usePrecisePointer } from '../lib/pointer.js';

export function cx(...values: (string | false | null | undefined)[]): string {
  return values.filter(Boolean).join(' ');
}

// ---------------------------------------------------------------------------
// Boton
// ---------------------------------------------------------------------------

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'warn';
type Size = 'sm' | 'md' | 'lg' | 'xl';

const VARIANTS: Record<Variant, string> = {
  primary:
    'bg-primary text-on-primary hover:bg-primary-hover active:brightness-95 shadow-card font-semibold',
  secondary:
    'bg-surface-2 text-ink border border-line hover:bg-surface-3 hover:border-line-strong',
  ghost: 'text-muted hover:text-ink hover:bg-surface-2',
  danger: 'bg-danger text-white hover:bg-danger-hover font-semibold',
  warn: 'bg-warn-soft text-warn border border-warn/30 hover:brightness-110 font-semibold',
};

const SIZES: Record<Size, string> = {
  sm: 'h-9 px-3 text-sm rounded-lg gap-1.5',
  md: 'h-11 px-4 text-[15px] rounded-xl gap-2',
  lg: 'h-13 px-5 text-base rounded-xl gap-2',
  xl: 'h-16 px-6 text-lg rounded-2xl gap-2.5',
};

/**
 * UN BOTON QUE ES UNA TECLA NO SE QUEDA CON EL FOCO.
 *
 * En Windows, hacer clic en un `<button>` lo enfoca. Eso no importaba mientras el
 * teclado no hiciera nada; con la capa de teclas (`lib/keys.ts`) importa mucho,
 * porque Enter con el foco en un boton es "pulsa ESE boton" y la capa se aparta a
 * proposito — si no, un Enter sobre "Cancelar" cancelaria Y confirmaria.
 *
 * O sea que sin esto: tocar el "5" del teclado en pantalla con el raton y luego
 * pulsar Enter NO HARIA NADA, porque el foco se quedo en el 5. Justo la mezcla
 * de raton y teclado de una all-in-one con teclado al lado.
 *
 * Va en los botones que son TECLAS —el teclado numerico, los atajos de cantidad,
 * los billetes— y NO en los botones de comando del pie, donde el foco es
 * correcto y Enter debe significar "pulsame".
 *
 * `onMouseDown` con `preventDefault` cancela solo el enfocado; el clic sigue
 * ocurriendo, `:active` sigue animando y el Tab del teclado sigue llegando.
 */
export const noFocus = {
  onMouseDown: (event: { preventDefault: () => void }) => event.preventDefault(),
} as const;

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  block?: boolean;
}

export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  block = false,
  className,
  children,
  disabled,
  ...rest
}: ButtonProps): ReactNode {
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      className={cx(
        'inline-flex items-center justify-center whitespace-nowrap',
        'transition-[background-color,border-color,transform,opacity] duration-150',
        'active:scale-[0.98] disabled:pointer-events-none disabled:opacity-45',
        VARIANTS[variant],
        SIZES[size],
        block && 'w-full',
        className,
      )}
    >
      {loading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Pista de tecla
// ---------------------------------------------------------------------------

/**
 * La tecla dibujada, y SOLO donde hay una tecla.
 *
 * Va DENTRO del boton principal —"Cobrar $1,260.00 ⏎"— y no en un renglon aparte
 * al pie de la hoja. Un renglon cuesta alto, y el alto es exactamente lo que
 * estas hojas no tienen de sobra (§9: en escritorio una hoja modal no se
 * desplaza, cabe entera). Dentro del boton cuesta ancho, que es lo que sobra, y
 * ademas se lee donde se va a usar.
 *
 * SE DECIDE EN JAVASCRIPT Y NO CON UNA VARIANTE DE TAILWIND, a proposito:
 * `pointer: fine` no es una variante de fabrica en v4, y una utilidad escrita a
 * mano dentro de `@layer utilities` no acepta prefijos — se ignoraria en silencio
 * (§13). El gancho ademas repinta cuando a un iPad le ponen teclado a media
 * sesion.
 *
 * `aria-hidden` porque es una PISTA, no informacion: el boton ya dice lo que
 * hace, y un lector de pantalla leyendo "flecha de retorno" despues de cada
 * etiqueta es ruido. Quien navegue con teclado igual descubre la tecla usandola.
 */
export function KeyHint({
  children = '⏎',
  tone = 'inherit',
  className,
}: {
  children?: ReactNode;
  /** `surface` para una pista suelta sobre el fondo; `inherit` dentro de un boton. */
  tone?: 'inherit' | 'surface';
  className?: string;
}): ReactNode {
  const precise = usePrecisePointer();
  if (!precise) return null;

  return (
    <kbd
      aria-hidden
      className={cx(
        'rounded border px-1 py-px text-[11px] font-medium leading-[1.4] not-italic',
        // Hereda el color del boton: un chip claro sobre el verde del primario se
        // ve como una etiqueta pegada, no como una tecla del mismo control.
        tone === 'inherit'
          ? 'border-current/35 opacity-75'
          : 'border-line bg-surface text-ink',
        className,
      )}
    >
      {children}
    </kbd>
  );
}

// ---------------------------------------------------------------------------
// Contenedores
// ---------------------------------------------------------------------------

export function Card({
  className,
  children,
  padded = true,
}: {
  className?: string;
  children: ReactNode;
  padded?: boolean;
}): ReactNode {
  return (
    /*
     * `min-w-0` NO es decorativo: es lo que impide que la pantalla se pueda
     * arrastrar de lado.
     *
     * Las tarjetas viven en rejillas (`grid lg:grid-cols-3 gap-4`), y en el
     * telefono esa rejilla es de una sola pista automatica. Una pista automatica
     * mide al menos el min-content de su celda, y varias tarjetas contienen una
     * tabla con `min-w-[34rem]` dentro de su envoltorio `overflow-x-auto`. Ese
     * envoltorio existe justamente para que la tabla se desplace por dentro,
     * pero el truco solo funciona si alguien le pone tope: sin `min-w-0` la
     * tarjeta se estiraba a 578 px dentro de una pantalla de 390, la rejilla con
     * ella, y como `main` desplaza el eje X, la aplicacion entera se movia de
     * lado unos 200 px. Medido en el Panel: la tabla NO se desplazaba por dentro
     * —se desplazaba la pantalla—, que es justo lo contrario del diseño.
     *
     * Va en la primitiva y no en cada pantalla porque el invariante es de la
     * tarjeta: una tarjeta nunca debe ensanchar a quien la contiene.
     */
    <section
      className={cx(
        'min-w-0 bg-surface border border-line rounded-2xl shadow-card',
        padded && 'p-4 sm:p-5',
        className,
      )}
    >
      {children}
    </section>
  );
}

export function SectionTitle({
  title,
  hint,
  action,
}: {
  title: string;
  hint?: string;
  action?: ReactNode;
}): ReactNode {
  return (
    <header className="flex items-start justify-between gap-3 mb-4">
      <div className="min-w-0">
        <h2 className="text-[15px] font-semibold tracking-tight text-ink">{title}</h2>
        {hint ? <p className="text-[13px] text-muted mt-0.5 text-pretty">{hint}</p> : null}
      </div>
      {action}
    </header>
  );
}

// ---------------------------------------------------------------------------
// Indicadores
// ---------------------------------------------------------------------------

type Tone = 'neutral' | 'primary' | 'warn' | 'danger' | 'info';

const TONES: Record<Tone, string> = {
  neutral: 'bg-surface-2 text-muted border-line',
  primary: 'bg-primary-soft text-primary border-primary/25',
  warn: 'bg-warn-soft text-warn border-warn/25',
  danger: 'bg-danger-soft text-danger border-danger/25',
  info: 'bg-info-soft text-info border-info/25',
};

export function Badge({
  tone = 'neutral',
  children,
  className,
}: {
  tone?: Tone;
  children: ReactNode;
  className?: string;
}): ReactNode {
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5',
        'text-[12px] font-medium leading-5 whitespace-nowrap',
        TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

/**
 * Tarjeta de cifra. El valor usa numeros tabulares y un tamaño grande porque
 * suele leerse de lejos, con el celular en la mano o desde el otro lado del
 * mostrador.
 */
export function Stat({
  label,
  value,
  hint,
  tone = 'neutral',
  icon,
  onClick,
}: {
  label: string;
  /** Admite un nodo para poder pasar una cifra que rueda al cambiar. */
  value: ReactNode;
  hint?: ReactNode;
  tone?: Tone;
  icon?: ReactNode;
  /**
   * Abre el desglose de la cifra.
   *
   * La tarjeta ENTERA es el area de toque, no una flecha en la esquina: en el
   * mostrador se opera con el pulgar y de pie, y un blanco de 16 px se falla.
   * Cuando la hay, la flecha es una pista visual, no el boton.
   */
  onClick?: () => void;
}): ReactNode {
  const valueTone =
    tone === 'primary'
      ? 'text-primary'
      : tone === 'danger'
        ? 'text-danger'
        : tone === 'warn'
          ? 'text-warn'
          : 'text-ink';

  return (
    /*
     * `min-w-0` es lo que impide que una cifra grande rompa la pantalla.
     *
     * Estas tarjetas viven en una rejilla, y una celda de rejilla mide por
     * omision al menos lo que mide su contenido sin partir. Un importe no tiene
     * espacios, asi que "no partir" quiere decir entero: con un valor de siete
     * cifras la celda se ensancha, empuja la rejilla, y la pagina entera gana
     * desplazamiento horizontal. Medido en un telefono de 320 px: la rejilla
     * pedia 318 px donde caben 288.
     *
     * Con `min-w-0` la celda se queda en su ancho y el problema baja al numero,
     * donde se resuelve sin esconder nada: ver la nota del importe.
     */
    <Wrap
      onClick={onClick}
      className="min-w-0 bg-surface border border-line rounded-2xl p-4 shadow-card"
    >
      <div className="flex items-center gap-2 text-muted">
        {icon}
        <span className="text-[12px] font-medium uppercase tracking-wider">{label}</span>
        {/* La flecha es una PISTA de que hay algo debajo, no el area de toque:
            esa es la tarjeta entera. Va al final del renglon del rotulo porque
            ahi no le quita ancho al importe, que es lo que se lee de lejos. */}
        {onClick ? <ChevronRight className="size-4 ml-auto shrink-0 opacity-60" /> : null}
      </div>
      {/*
       * El tamaño del importe sale de medir, no de elegir a ojo.
       *
       * Estas tarjetas van de dos en dos hasta `lg`. El interior de una tarjeta
       * mide 106 px en una pantalla de 320, 126 en una de 360 y 133 en una de
       * 375. Medido con esta misma tipografia, un importe de cinco cifras
       * ("$15,430.50", que en una tienda es el total de un martes cualquiera)
       * ocupa 112 px a 24 px de tamaño y 93 a 20 px.
       *
       * O sea que a 24 px NO cabia en un telefono de 320: el numero de todos los
       * dias se partia en dos renglones. Bajando el escalon mas chico a 20 px
       * entra hasta un importe de seis cifras, y desde 360 px de ancho se vuelve
       * a los 24. Sigue siendo tipografia grande —la regla 1 de la hoja de
       * estilos— solo que del tamaño que de verdad cabe.
       *
       * `overflow-wrap: anywhere` queda como ultimo recurso para lo que ninguna
       * tienda vera: siete cifras. Se prefiere partir el numero antes que
       * recortarlo con puntos suspensivos, porque a un total al que le falta un
       * digito es peor que no haberlo puesto. Y es lo que ademas permite que
       * `min-w-0` funcione: sin el, el minimo de la celda seguiria siendo el
       * numero entero.
       */
      }
      <p
        className={cx(
          'tabular mt-2 text-xl min-[360px]:text-2xl sm:text-[28px] font-semibold tracking-tight',
          '[overflow-wrap:anywhere]',
          valueTone,
        )}
      >
        {value}
      </p>
      {hint ? <div className="mt-1 text-[13px] text-muted">{hint}</div> : null}
    </Wrap>
  );
}

/**
 * La tarjeta de cifra, con o sin desglose detras.
 *
 * Se separa para que el cuerpo se escriba UNA vez. Escribirlo dos —una version
 * `div` y otra `button`— es como se acaban desincronizando: alguien ajusta el
 * tamaño del importe en una y la otra se queda con el de antes.
 *
 * El `button` no reproduce el aspecto: hereda las mismas clases y solo agrega
 * lo que un boton necesita —alineacion del texto a la izquierda, que en un
 * boton es al centro por omision, y el realce al pasar por encima—.
 */
function Wrap({
  onClick,
  className,
  children,
}: {
  onClick?: () => void;
  className: string;
  children: ReactNode;
}): ReactNode {
  if (!onClick) return <div className={className}>{children}</div>;
  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        className,
        'text-left w-full hover:border-line-strong hover:brightness-110',
        'transition-[filter,border-color]',
      )}
    >
      {children}
    </button>
  );
}

export function Spinner({ className }: { className?: string }): ReactNode {
  return <Loader2 className={cx('size-5 animate-spin text-muted', className)} aria-hidden />;
}

export function LoadingBlock({ label = 'Cargando...' }: { label?: string }): ReactNode {
  return (
    <div className="flex items-center justify-center gap-3 py-16 text-muted">
      <Spinner />
      <span className="text-sm">{label}</span>
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
}): ReactNode {
  return (
    <div className="flex flex-col items-center justify-center text-center py-14 px-6">
      {icon ? <div className="text-subtle mb-3">{icon}</div> : null}
      <p className="text-ink font-medium">{title}</p>
      {description ? (
        <p className="text-[13px] text-muted mt-1 max-w-sm text-pretty">{description}</p>
      ) : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

/**
 * Una lectura que FALLA no es una lista VACIA.
 *
 * Es la diferencia entre "hoy no has vendido nada" y "no pude preguntar", y en
 * una caja registradora esa diferencia es que el dueño crea que perdio el
 * dinero del dia. Paso de verdad: una consulta rota al historial contestaba
 * exactamente igual que un dia sin ventas —"Sin ventas registradas"— mientras
 * las ventas estaban guardadas, cobradas y cuadradas en la base.
 *
 * Por eso ningun listado de esta aplicacion vuelve a pintar su estado vacio
 * cuando la consulta trono. Y por eso el texto dice, con todas sus letras, que
 * lo que fallo fue LEER: una lectura no pierde nada, y quien esta del otro lado
 * del mostrador necesita saber eso antes que ninguna otra cosa.
 */
export function ErrorState({
  error,
  onRetry,
  retrying = false,
  what = 'la informacion',
}: {
  error: unknown;
  onRetry?: () => void;
  retrying?: boolean;
  /** Que no se pudo cargar, para completar "No se pudo cargar ___". */
  what?: string;
}): ReactNode {
  // `ApiError` extiende `Error`, asi que el mensaje ya viene en español sin
  // que estas primitivas tengan que importar la pasarela de datos.
  const detail = error instanceof Error ? error.message.trim() : '';

  return (
    <div className="flex flex-col items-center justify-center text-center py-14 px-6">
      <div className="text-danger mb-3" aria-hidden>
        <AlertTriangle className="size-8" />
      </div>
      <p className="text-ink font-medium">No se pudo cargar {what}</p>
      <p className="text-[13px] text-muted mt-1 max-w-sm text-pretty">
        Fallo la consulta, no los datos: lo que ya se cobro sigue registrado. Revisa tu conexion
        y vuelve a intentar.
      </p>
      {detail ? (
        <p className="text-[12px] text-subtle mt-2 max-w-sm [overflow-wrap:anywhere]">{detail}</p>
      ) : null}
      {onRetry ? (
        <div className="mt-4">
          <Button variant="secondary" onClick={onRetry} loading={retrying}>
            <RefreshCw className="size-4" />
            Reintentar
          </Button>
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Formularios
// ---------------------------------------------------------------------------

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string | undefined;
  hint?: string;
  prefix?: string;
}

export function Field({ label, error, hint, prefix, className, ...rest }: FieldProps): ReactNode {
  const id = useId();
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-[13px] font-medium text-muted">
        {label}
      </label>
      <div className="relative">
        {prefix ? (
          <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted pointer-events-none">
            {prefix}
          </span>
        ) : null}
        <input
          {...rest}
          id={id}
          aria-invalid={error ? true : undefined}
          className={cx(
            'w-full h-12 rounded-xl bg-surface-2 border text-ink placeholder:text-subtle',
            'px-3.5 text-[15px] transition-colors',
            'focus:border-primary focus:bg-surface outline-none',
            prefix && 'pl-8',
            error ? 'border-danger' : 'border-line',
            className,
          )}
        />
      </div>
      {error ? (
        <p className="text-[12px] text-danger">{error}</p>
      ) : hint ? (
        <p className="text-[12px] text-subtle">{hint}</p>
      ) : null}
    </div>
  );
}

export function Select({
  label,
  children,
  error,
  ...rest
}: {
  label: string;
  error?: string;
  children: ReactNode;
} & InputHTMLAttributes<HTMLSelectElement>): ReactNode {
  const id = useId();
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-[13px] font-medium text-muted">
        {label}
      </label>
      <select
        {...(rest as object)}
        id={id}
        className={cx(
          'w-full h-12 rounded-xl bg-surface-2 border text-ink px-3 text-[15px]',
          'focus:border-primary focus:bg-surface outline-none appearance-none',
          error ? 'border-danger' : 'border-line',
        )}
      >
        {children}
      </select>
      {error ? <p className="text-[12px] text-danger">{error}</p> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------

/**
 * Hoja modal.
 *
 * En movil sube desde abajo (donde alcanza el pulgar) y en pantallas grandes se
 * centra. Cierra con Escape y bloquea el desplazamiento del fondo, que si no
 * hace que la pagina de atras se mueva bajo el dedo.
 *
 * EN ESCRITORIO LA HOJA NO SE DESPLAZA: CABE ENTERA.
 * --------------------------------------------------
 * En el telefono desplazar una hoja es natural —se hace con el pulgar, sobre el
 * mismo dedo que ya esta ahi—. En la all-in-one del mostrador no: la persona
 * esta de pie, con el cliente enfrente, y lo que necesita ver casi siempre es lo
 * de ABAJO —el teclado, el boton de cobrar—, o sea justo lo que el corte
 * esconde. Peor todavia, no se ve que falte nada: una barra de desplazamiento
 * fina sobre fondo oscuro no la registra nadie con prisa.
 *
 * Medido en el banco de pruebas a 1366x700, que es lo que deja una pantalla de
 * 1366x768 con la barra del navegador: TODAS las hojas se cortaban. La cantidad
 * de granel se pasaba 91 px, el cobro 72, la apertura de caja 39 y el alta de
 * producto 548.
 *
 * La salida no es apretar el contenido: es que en escritorio SOBRA ANCHO y
 * NUNCA sobra alto. Por eso las hojas altas se reparten en dos columnas con
 * `SheetSplit` (ver abajo) en vez de crecer hacia abajo.
 *
 * Lo que le toca a esta pieza son las dos condiciones para que eso sea posible:
 *
 *   1. **El tope de alto es un margen fijo, no un porcentaje**, en cuanto la
 *      hoja deja de subir desde abajo y se centra. Un 92 % regala 28 px arriba y
 *      28 abajo en una pantalla de 700 y 43 en una de 1080: aire que no hace
 *      falta y que a la hoja si. Los 16 px de guarda son los mismos en toda
 *      pantalla, que es como se ve un dialogo de escritorio bien puesto, y son
 *      justo lo que el cobro necesitaba para dejar de rozar el borde: con el 92 %
 *      quedaba en 650 de 652.
 *   2. **El ancho lo elige la hoja** con `size`, y una hoja de dos columnas
 *      necesita `md` como minimo: partir `sm` (448 px) deja columnas de 196 px,
 *      donde el teclado numerico ya no se puede tocar.
 */
export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  onEnter,
  onSpace,
  onPad,
  enterInFields = true,
  bare = false,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  /** `xl` es para las hojas de dos paneles de la all-in-one. */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /**
   * La accion PRINCIPAL de la hoja, la del boton del pie.
   *
   * Va aqui y no en cada boton porque la hoja es quien sabe cual de sus botones
   * es el principal, y porque asi la regla —Enter confirma— se escribe una vez.
   * Tiene que llevar LA MISMA guarda que apaga ese boton: `onEnter` esquiva el
   * DOM, asi que el `disabled` del `Button` no lo protege y dos Enters seguidos
   * serian dos mutaciones.
   *
   * Las hojas DESTRUCTIVAS no lo pasan, a proposito: un Enter perdido sobre un
   * primario `danger` borra datos sin un solo gesto de apuntador.
   */
  onEnter?: () => void;
  /** La segunda salida, donde la hoja tiene dos (imprimir, ver el ticket). */
  onSpace?: () => void;
  /**
   * El teclado fisico escribiendo en el teclado EN PANTALLA de la hoja.
   *
   * Lo arma `useKeypad` (components/NumericPad.tsx) y viaja por aqui, no
   * registrandose por su cuenta: el cuerpo de la hoja se escribe una capa mas
   * abajo de donde vive, asi que un registro hecho en el componente de la hoja
   * caeria en la capa de la PANTALLA y quedaria por debajo de su propia hoja.
   */
  onPad?: (key: string, code: string) => void;
  /** Ver `KeyBindings.enterInFields`: `false` en las hojas-documento. */
  enterInFields?: boolean;
  /**
   * LA HOJA NO PINTA NADA: la pinta su hijo.
   *
   * Se va el encabezado con su titulo y su filete, y se va tambien la
   * superficie —fondo, borde, sombra, esquinas y la entrada—; lo unico que
   * queda es el andamio que no se puede reponer desde afuera: el portal, el
   * Escape, el foco, el bloqueo del lector y el tope de alto. El boton de
   * cerrar pasa a flotar sobre el contenido.
   *
   * Existe por UNA pieza: la tarjeta de presentacion de quien hizo el sistema,
   * que ES una tarjeta —con su canto, su material y su inclinacion en 3D— y no
   * un recuadro dentro de un dialogo. Dibujada adentro de la hoja normal se
   * veian DOS cajas oscuras redondeadas, una dentro de la otra, y un titulo
   * que repetia la palabra que el logotipo ya dice tres centimetros abajo.
   *
   * Y va CENTRADA tambien en el telefono: una hoja que sube desde abajo es un
   * panel de acciones; una tarjeta se mira, asi que se queda en medio.
   *
   * NO es para hojas de captura. Sin encabezado no hay donde poner el titulo, y
   * el titulo de una hoja de captura es lo que dice que se esta haciendo.
   */
  bare?: boolean;
}): ReactNode {
  const panelRef = useRef<HTMLDivElement | null>(null);
  /** El cuerpo que se desplaza. Se mide para saber si queda algo por leer. */
  const bodyRef = useRef<HTMLDivElement | null>(null);

  /*
   * El Escape ya no es un oyente propio: lo atiende la capa de teclas, que es
   * la que sabe QUE HOJA ESTA ENCIMA.
   *
   * Antes era un `document.addEventListener` por hoja abierta y ciego a capas,
   * asi que con dos hojas apiladas —Inventario -> producto -> Editar, o la
   * entrada de mercancia y su acuse— un solo Escape las cerraba LAS DOS. Ahora
   * la de arriba se lo queda.
   */
  useFrameKeys({ onEscape: onClose, onEnter, onSpace, onPad, enterInFields }, open);

  useEffect(() => {
    if (!open) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  /*
   * El foco entra al panel para que el teclado y el lector de pantalla no se
   * queden detras del fondo. Va en su PROPIO efecto, y solo depende de `open`.
   *
   * Junto al de arriba dependia tambien de `onClose`, que casi siempre es una
   * flecha nueva en cada render del padre: bastaba con que la pantalla de atras
   * se repintara —una invalidacion de Realtime, otra caja vendiendo— para que
   * el panel se robara el foco del campo que se estaba escribiendo. Con un
   * nombre a medias en el teclado del telefono, eso se ve como que la
   * aplicacion se traga las letras.
   */
  useEffect(() => {
    if (open) panelRef.current?.focus();
  }, [open]);

  /*
   * UNA HOJA ABIERTA SE QUEDA CON EL LECTOR, aunque no sepa que hacer con el.
   *
   * Antes cada pantalla apagaba su lector USB a mano enumerando las hojas que
   * podia tener abiertas —`sheet === null && restock === null && !creating &&
   * !labelsOpen && !cameraOpen`—, y olvidar una dejaba un escaneo agregando
   * productos DETRAS de la hoja, con el codigo colandose ademas en el campo
   * enfocado: un peso o un costo con trece digitos de mas que nadie vuelve a
   * mirar. La regla vive aqui, una sola vez, porque la condicion no es de
   * ninguna pantalla en particular: es de "hay una hoja encima".
   *
   * Las hojas que SI escanean —el alta, la entrada de mercancia— registran su
   * propio oyente dentro de esta misma capa y le ganan al bloqueo. Ver
   * `useScanBlocker` y `topmost` en `lib/scanner.ts`.
   */
  const scanLayer = useScanLayer() + 1;
  useScanBlocker(open);

  /*
   * "HAY MAS ABAJO": LA SEÑAL QUE FALTABA.
   *
   * Un corte limpio a media hoja no se lee como corte: se lee como final. En el
   * telefono la hoja llega hasta el filo del pie, sin barra de desplazamiento
   * que asome —iOS y Android las esconden hasta que algo se mueve— asi que lo
   * que queda debajo no existe hasta que alguien deslice por casualidad.
   *
   * Lo reporto el dueño sobre el alta de personal, y la frase importa: "ni yo
   * que lo cree lo veo". Medido ahi mismo en un iPhone de 390x844, la hoja
   * escondia 200 px —dos campos del formulario— y el boton de crear la cuenta
   * salia apagado esperando justo lo que no se veia. La hoja no se veia rota:
   * se veia TERMINADA, que es la forma cara de fallar.
   *
   * Va en `Modal` y no en esa hoja porque la trampa no es de esa pantalla: es
   * de cualquier hoja mas alta que la ventana, y son casi todas en el telefono.
   *
   * Tres decisiones:
   *
   *   - **Se apaga al llegar al fondo**, asi que no es un adorno permanente:
   *     mientras se vea, de verdad queda algo por leer.
   *   - **No atrapa el dedo** (`pointer-events-none`). Se dibuja ENCIMA del
   *     ultimo renglon visible, y un boton flotante ahi taparia un campo o un
   *     control de la hoja. Lo que hace falta es enterarse; deslizar ya se sabe.
   *   - **Se remide con lo que de verdad la cambia**: el desplazamiento, el
   *     tamaño del hueco (`ResizeObserver`) y el contenido que crece o aparece
   *     sin cambiarle el tamaño al hueco —un aviso de error, una lista que
   *     llega— con un `MutationObserver`. Sin el tercero, la hoja que estrena
   *     contenido despues de abrirse se queda sin señal.
   */
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    const el = bodyRef.current;
    if (!open || el === null) return;

    // Un pelo de tolerancia: el redondeo de un zoom del navegador deja sobras de
    // menos de un pixel y sin esto la señal se queda prendida en el fondo.
    const measure = (): void => setHasMore(el.scrollHeight - el.clientHeight - el.scrollTop > 4);

    measure();
    el.addEventListener('scroll', measure, { passive: true });
    const resize = new ResizeObserver(measure);
    resize.observe(el);
    const mutate = new MutationObserver(measure);
    mutate.observe(el, { childList: true, subtree: true, characterData: true });

    return () => {
      el.removeEventListener('scroll', measure);
      resize.disconnect();
      mutate.disconnect();
    };
  }, [open]);

  if (!open) return null;

  /*
   * El ancho crece con la pantalla, en tres escalones.
   *
   * Un solo maximo fijo se ve chico en cuanto la pantalla es grande: medido a
   * 1266x827 —la all-in-one del mostrador— la hoja de cantidad ocupaba el 45 %
   * del ancho y el 67 % del alto, con la mitad de la pantalla en negro alrededor
   * de un teclado de teclas chicas. En una maquina que solo se toca, ese hueco
   * no es sobriedad: es area de toque tirada a la basura.
   *
   * Los escalones son de ANCHO, asi que no cuestan alto y van con `lg:`/`2xl:`
   * a secas. Lo que si cuesta alto —el tamaño de las teclas y de las cifras—
   * escala con `lg:xtall:`, exigiendo ademas que haya alto de sobra. Ver `xtall`
   * en styles/theme.css, donde esta la cuenta del corte.
   *
   *   size |  sm: (>=640) | lg: (>=1024) | 2xl: (>=1536)
   *   -----+--------------+--------------+--------------
   *   sm   |  448         |  576         |  672
   *   md   |  576         |  896         | 1024
   *   lg   |  768         | 1024         | 1280
   *   xl   |  768         | 1024         | 1440
   *
   * El escalon de `lg:` se topa en 1024 aunque la pantalla de al lado ya sea de
   * 1152: en un monitor de 1266 px eso es el 81 % del ancho, y por encima de ahi
   * la hoja deja de leerse como una ventana secundaria y parece que cambiaste de
   * pantalla. 1152 daba 91 % y se veia justo asi. El paso a 1440 espera a `2xl`,
   * donde vuelve a ser tres cuartos de la pantalla.
   *
   * Y una hoja ancha NO estira lo que no debe estirarse: `SheetSplit` con
   * `keypad` le pone tope a la columna del teclado y el ancho que sobra se lo
   * queda la columna que se lee, que es la que gana con el.
   *
   * Los escalones se subieron una segunda vez despues de verlos en la maquina
   * del mostrador: con la tabla anterior el acuse de una venta ocupaba el 27 %
   * del ancho de la pantalla y la lista de clientes el 42 %, o sea recuadros
   * chicos en medio de un monitor enorme, que en la practica obliga a acercarse.
   * Que se note que son pantallas secundarias esta bien; que haya que forzar la
   * vista, no.
   *
   * `sm` sigue siendo el mas conservador —son avisos y confirmaciones de dos
   * renglones, y una linea de texto de 1200 px de largo se lee peor, no mejor—
   * pero 448 px fijos eran muy poco: 672 es aproximadamente el ancho de columna
   * comodo para leer, que es el limite util de un dialogo de texto.
   */
  const widths = {
    sm: 'sm:max-w-md lg:max-w-xl 2xl:max-w-2xl',
    md: 'sm:max-w-xl lg:max-w-4xl 2xl:max-w-5xl',
    lg: 'sm:max-w-3xl lg:max-w-5xl 2xl:max-w-7xl',
    xl: 'sm:max-w-3xl lg:max-w-5xl 2xl:max-w-[90rem]',
  };

  /*
   * La hoja se monta en `document.body`, NO donde se escribio.
   *
   * `position: fixed` no siempre se resuelve contra la ventana: cualquier
   * ancestro con `transform`, `filter`, `perspective`, `contain` o
   * `backdrop-filter` se vuelve su bloque contenedor. El encabezado movil lleva
   * `glass`, que es `backdrop-filter: blur(...)`, y el selector de sucursal vive
   * DENTRO de ese encabezado: sin el portal, `inset-0` medi­a los 115 px del
   * encabezado en vez de los 812 de la pantalla, asi que con `items-end` la hoja
   * se alineaba al fondo de esa franja y se desbordaba HACIA ARRIBA, cortada por
   * la muesca. El fondo oscuro tampoco tapaba la pagina —solo el encabezado—, de
   * modo que tocar fuera no cerraba.
   *
   * Se arregla aqui y no en el selector porque la trampa no es de esa pantalla:
   * es de cualquier hoja que algun dia se escriba dentro de un elemento con
   * filtro. Montada en `body` no hay ancestro que la contenga.
   */
  return createPortal(
    /*
     * El contenido de la hoja vive una capa MAS ARRIBA, y el portal no cambia
     * eso: el contexto de React viaja por el arbol donde se ESCRIBIO, no por
     * donde se monta. Es lo que hace que un `useUsbScanner` de adentro le gane
     * al bloqueo de la hoja y a la pantalla de atras, sin que ninguno de los
     * tres sepa de los otros.
     */
    <ScanLayerContext.Provider value={scanLayer}>
      <div
        /*
          La guarda de 16 px vive AQUI y solo de `sm:` para arriba. En el telefono
          la hoja va pegada a los bordes —asi se ve una hoja que sube desde abajo—
          pero centrada tiene que respirar: sin esto, una hoja `xl` en una ventana
          de 1024 px de ancho quedaba tocando los dos filos del cristal, que es
          justo lo que hace que un dialogo se vea sin terminar.
  
          El relleno no encoge al fondo oscuro: para un hijo absoluto el bloque
          contenedor es el borde del RELLENO del ancestro, asi que `inset-0` sigue
          cubriendo la pantalla entera y tocar fuera —tambien en la franja de la
          guarda— sigue cerrando.
  
          Son los mismos 16 px que descuenta el tope de alto del panel (`-2rem`
          arriba y abajo), para que la hoja quede centrada con la misma guarda por
          los cuatro lados y no con una arriba y otra a los costados.
        */
        className={cx(
          'fixed inset-0 z-50 flex justify-center',
          bare ? 'items-center p-4' : 'items-end sm:items-center sm:p-4',
        )}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <button
          type="button"
          aria-label="Cerrar"
          className="absolute inset-0 bg-black/60 backdrop-blur-[2px] animate-in-fade"
          onClick={onClose}
        />
        <div
          ref={panelRef}
          tabIndex={-1}
          className={cx(
            'relative w-full outline-none',
            // Con `bare` la superficie es del hijo: ni fondo, ni borde, ni sombra,
            // ni esquinas, ni entrada propia —el hijo trae la suya y dos
            // animaciones encimadas se pelean—.
            !bare && 'bg-surface border border-line shadow-float',
            // El tope usa `--app-vh`, no `dvh` suelto: en la PWA de iOS `dvh` no
            // queda inicializada en el arranque en frio —nace corta— y la hoja
            // quedaba con una franja muerta debajo del pie. `--app-vh` es `100vh`
            // en modo instalado y `100dvh` en el navegador, que es lo correcto en
            // cada uno. Ver la nota larga en `h-app` (theme.css).
            //
            // El 92 % es para la hoja que SUBE DESDE ABAJO: deja ver un pedazo de
            // la pantalla de atras, que es lo que dice "esto se cierra deslizando".
            // Centrada ya no hace falta esa pista y el porcentaje solo regala aire
            // (28 px arriba y 28 abajo en una pantalla de 700), asi que ahi la
            // guarda pasa a ser fija. Ver la nota de arriba.
            'flex flex-col',
            !bare && 'rounded-t-2xl sm:rounded-2xl animate-in-up',
            'max-h-[calc(var(--app-vh,100dvh)*0.92)] sm:max-h-[calc(var(--app-vh,100dvh)-2rem)]',
            widths[size],
          )}
        >
          {/*
            Los laterales llevan piso de area segura en las tres franjas de la
            hoja. La hoja sigue pegada a los bordes en el telefono —asi se ve una
            hoja que sube desde abajo— pero su CONTENIDO se aparta de la muesca
            cuando el aparato esta acostado. Sin esto, con el telefono girado el
            titulo y el boton de cerrar quedan debajo del vidrio negro.
          */}
          {/*
            El relleno lateral sube un escalon en pantalla grande —`px-safe-7`, o
            sea 28 px contra 20— en las tres franjas. Es aire GRATIS: cuesta ancho,
            que es justo lo que sobra, y sin el una hoja de 1024 px con el contenido
            a 20 px del filo se ve apretada aunque quepa. El relleno VERTICAL no
            sube: ahi cada pixel se lo pelea el contenido, y la hoja de cobro ya
            termina a 22 px del borde en una all-in-one de 1366x768.
          */}
          {bare ? (
            /* Sin encabezado, el boton de cerrar FLOTA sobre el contenido. `z-10`
               lo deja por encima de la superficie que pinta el hijo; el titulo
               sigue existiendo para el lector de pantalla en el `aria-label` del
               dialogo, que es donde de verdad hace falta. */
            <button
              type="button"
              onClick={onClose}
              aria-label="Cerrar"
              className="touch-target absolute right-1 top-1 z-10 grid place-items-center rounded-xl text-muted hover:text-ink"
            >
              <X className="size-5" />
            </button>
          ) : (
            <header className="flex items-center justify-between gap-3 px-safe-5 lg:px-safe-7 py-4 border-b border-line">
              <h2 className="text-base lg:text-lg font-semibold text-ink truncate">{title}</h2>
              <button
                type="button"
                onClick={onClose}
                aria-label="Cerrar"
                className="touch-target -mr-2 grid place-items-center rounded-xl text-muted hover:text-ink hover:bg-surface-2"
              >
                <X className="size-5" />
              </button>
            </header>
          )}
  
          {/*
            `min-h-0` no sobra por mas que `overflow-y-auto` ya baje el minimo
            automatico a cero: lo deja escrito para quien lea el archivo, igual que
            en el resto del marco.
  
            Cuando NO hay pie, el ultimo renglon del cuerpo es lo que llega al
            borde inferior de la pantalla —la hoja sube desde abajo en el
            telefono—, asi que el margen del sistema le toca a el. Sin esto, en un
            iPhone la ultima linea queda debajo de la franja del gesto de inicio y
            parece que la hoja se corto.
          */}
          <div
            ref={bodyRef}
            className={cx(
              'overflow-y-auto grow min-h-0',
              // Con `bare` el relleno es del hijo: el suyo es el de la tarjeta,
              // y sumarle el de la hoja la dejaria flotando en un marco.
              !bare && 'px-safe-5 lg:px-safe-7 pt-4',
              !bare && (footer ? 'pb-4' : 'pb-safe-4'),
            )}
          >
            {children}

            {/*
              La señal se pega al fondo del hueco con `sticky`, no con
              `absolute`: absoluta se quedaria clavada al final del CONTENIDO
              —o sea invisible justo cuando hace falta, que es al principio— y
              habria que reponer a mano el desplazamiento.

              `-mt-12` contra `h-12` la deja ocupando CERO en el flujo, asi que
              no alarga la hoja ni empuja nada: solo se encima sobre los ultimos
              48 px. Y con `bare` no se dibuja, porque ahi la superficie es del
              hijo y el degradado tiraria a un color que no es el suyo.
            */}
            {!bare ? (
              <div
                aria-hidden
                className={cx(
                  'pointer-events-none sticky bottom-0 -mt-12 h-12 flex items-end justify-center',
                  'bg-gradient-to-t from-surface via-surface/80 to-transparent',
                  'transition-opacity duration-200',
                  hasMore ? 'opacity-100' : 'opacity-0',
                )}
              >
                <span className="grid size-7 place-items-center rounded-full border border-line bg-surface-2 text-muted shadow-card">
                  <ChevronDown className="size-4" />
                </span>
              </div>
            ) : null}
          </div>
  
          {footer ? (
            <footer className="px-safe-5 lg:px-safe-7 pt-4 pb-safe-4 border-t border-line flex gap-3 justify-end">
              {footer}
            </footer>
          ) : null}
        </div>
      </div>
    </ScanLayerContext.Provider>,
    document.body,
  );
}

/**
 * El cuerpo de una hoja, en dos columnas cuando hay ancho.
 *
 * Es la unica forma de que una hoja alta quepa entera en el mostrador sin
 * quitarle nada: lo que en el telefono es una lista larga que se desplaza con el
 * pulgar, en escritorio se reparte a lo ancho, que es la medida que sobra. Ver
 * la nota de `Modal`.
 *
 * EL REPARTO NO SE HACE POR ALTURA: SE HACE POR SENTIDO. Cortar la lista por la
 * mitad deja medio recuadro en cada columna y obliga a leer en zigzag.
 *
 *   - En una hoja de captura —cantidad, cobro, corte— izquierda lo que SE LEE
 *     (el precio, la cantidad, el cambio) y derecha lo que SE TOCA (los atajos,
 *     el teclado). Asi la mano tapa la columna de los botones y no la de los
 *     numeros, que es la que hay que seguir viendo mientras se teclea. Es el
 *     reparto que ya tenia la hoja de cobro y que aqui se escribe una sola vez.
 *   - En un formulario, cada columna se queda con secciones COMPLETAS. Lo que
 *     crece mientras se captura —una calculadora que aparece al escribir el
 *     costo, un aviso de margen— va todo en la misma columna, para que al
 *     teclear no salte la otra.
 *
 * Tres detalles que parecen de forma y no lo son:
 *
 *   - **El corte es de ANCHO (`sm:`) y esta bien que lo sea**, aunque CLAUDE.md
 *     avise de colgar decisiones de alto de los cortes de ancho. La advertencia
 *     es para lo que CUESTA alto; partir en columnas lo AHORRA, asi que el caso
 *     que mas lo necesita —el telefono acostado, 844 x 390— es justo el que
 *     entra por `sm:`.
 *   - **`minmax(0, …)` en las dos pistas.** Sin eso una pista mide al menos su
 *     contenido sin partir, y basta un importe o un nombre largo para que la
 *     columna crezca por encima de la hoja y arrastre a su vecina.
 *   - **Las columnas miden lo mismo y son `flex`**, para que la corta pueda
 *     llenar el sobrante en vez de dejar un hueco negro. Quien quiera aprovecharlo
 *     le pone `grow` a la pieza que deba estirarse —en la hoja de cantidad es el
 *     recuadro de la cifra, que es lo que el cliente lee desde el otro lado del
 *     mostrador—. Sin nadie que crezca, todo queda arriba y se ve igual que con
 *     `items-start`.
 *
 * En el telefono no hay rejilla: sale una sola columna, primero `children` y
 * despues `aside`, en el mismo orden en que se escriben.
 */
export function SheetSplit({
  children,
  aside,
  keypad = false,
  from = 'sm',
}: {
  /** Columna izquierda: lo que se lee, o las primeras secciones. Encabeza apilado. */
  children: ReactNode;
  /** Columna derecha: lo que se toca —atajos, teclado— o lo que crece al capturar. */
  aside: ReactNode;
  /**
   * La columna derecha es un teclado numerico.
   *
   * Un teclado tiene un ancho comodo y ahi se acaba: mas alla, las teclas se
   * vuelven rectangulos cada vez mas aplastados sin ganar nada —en una hoja de
   * cobro de 1440 px, media hoja son 692 y la tecla quedaria de 225 x 80—. Con
   * esto la pista se topa en 24rem y **el ancho que sobra se lo queda la columna
   * que se lee**, que si lo aprovecha: la cifra del peso y el cambio a devolver
   * son lo que alguien lee desde el otro lado del mostrador.
   *
   * El tope es `min(50%, 24rem)` y no `24rem` a secas para que en las hojas
   * angostas siga siendo un reparto a la mitad: con un tope duro, una hoja de
   * 576 px le daria 384 al teclado y 136 a las cifras.
   */
  keypad?: boolean;
  /**
   * Desde donde se parte en dos.
   *
   * `sm` (por omision) sirve a las hojas que se parten para no crecer de alto:
   * ahi cualquier ancho de mas ya es ganancia. `lg` es para las que se parten
   * solo para GANAR LEGIBILIDAD, y esas tienen que esperar a que la hoja de
   * verdad se ensanche: una hoja `md` mide 576 px entre `sm` y `lg`, asi que
   * partirla ahi deja columnas de 259 px —medido en el iPad de la tienda, que
   * pinta a ~700 px de ancho— con 950 px de alto sin usar. Apretar el texto
   * para no gastar un alto que sobra es el negocio al reves.
   */
  from?: 'sm' | 'lg';
}): ReactNode {
  return (
    <div
      className={cx(
        'grid gap-4 sm:items-stretch',
        keypad
          ? 'sm:grid-cols-[minmax(0,1fr)_minmax(0,min(50%,24rem))]'
          : from === 'lg'
            ? 'lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]'
            : 'sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]',
      )}
    >
      <div className="min-w-0 flex flex-col gap-4">{children}</div>
      <div className="min-w-0 flex flex-col gap-4">{aside}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mensajes
// ---------------------------------------------------------------------------

export function Alert({
  tone = 'danger',
  title,
  children,
}: {
  tone?: Tone;
  title?: string;
  children: ReactNode;
}): ReactNode {
  return (
    <div className={cx('rounded-xl border px-4 py-3 text-[13px]', TONES[tone])}>
      {title ? <p className="font-semibold mb-0.5">{title}</p> : null}
      <div className="text-pretty">{children}</div>
    </div>
  );
}

/**
 * UNA AYUDA QUE SE PRENDE, con su interruptor a la vista.
 *
 * Existe porque un formulario con ayudas siempre abiertas no se lee como "esto
 * es opcional": se lee como mas campos que llenar. En el alta de producto pasaba
 * justo eso — la cuenta del bulto y la calculadora de ganancia estaban ahi
 * siempre, mezcladas con el costo y el precio, y quien capturaba no sabia cuales
 * tenia que contestar. El interruptor lo dice sin explicarlo: apagado es "esto
 * no lo necesito", prendido es "ayudame con esto".
 *
 * Apagado ocupa un renglon y NO desaparece: seguir viendo el nombre es lo que
 * hace que alguien lo descubra el dia que lo necesita. Una ayuda escondida
 * detras de un menu es una ayuda que no existe.
 *
 * Es un `button` de verdad con `role="switch"` y `aria-checked`, no un `div` con
 * clases: asi lo anuncian los lectores de pantalla y responde a la barra
 * espaciadora sin que haya que programarlo.
 */
export function OptionalPanel({
  icon,
  label,
  hint,
  on,
  onChange,
  children,
}: {
  icon?: ReactNode;
  label: string;
  hint?: string;
  on: boolean;
  onChange: (on: boolean) => void;
  /**
   * Opcional: sin hijos queda un interruptor pelado, para cuando lo que se
   * prende no es un formulario sino un efecto que ya se ve en otro lado —el
   * mayoreo de la hoja de cobro cambia el total de arriba—. Se comparte el mismo
   * control a proposito: dos interruptores dibujados distinto en la misma
   * aplicacion se leen como dos cosas distintas.
   */
  children?: ReactNode;
}): ReactNode {
  return (
    <div
      className={cx(
        'rounded-xl border transition-colors',
        on ? 'border-primary/40 bg-primary-soft/20' : 'border-line bg-surface-2',
      )}
    >
      <button
        type="button"
        role="switch"
        aria-checked={on}
        onClick={() => onChange(!on)}
        className="w-full flex items-center gap-3 px-3.5 py-3 text-left"
      >
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1.5 text-[14px] font-medium">
            {icon ? <span className="shrink-0 text-muted">{icon}</span> : null}
            {label}
          </span>
          {hint ? (
            <span className="block text-[12px] text-muted mt-0.5 text-pretty">{hint}</span>
          ) : null}
        </span>

        {/* El riel. `shrink-0` porque una etiqueta larga lo aplastaria hasta
            dejarlo sin forma de interruptor, que es lo unico que lo explica. */}
        <span
          aria-hidden
          className={cx(
            'shrink-0 relative h-6 w-11 rounded-full transition-colors',
            on ? 'bg-primary' : 'bg-surface-3 border border-line',
          )}
        >
          <span
            className={cx(
              'absolute top-0.5 size-5 rounded-full bg-surface shadow-card transition-[left]',
              on ? 'left-[1.375rem]' : 'left-0.5',
            )}
          />
        </span>
      </button>

      {on && children ? <div className="px-3.5 pb-3.5 pt-0.5 space-y-3">{children}</div> : null}
    </div>
  );
}
